import { z } from "zod/v4";
import { useZodValidation } from "@/features/core-shared/composables/useZodValidation";
import { useComplexModelModelWithChanges } from "@/features/core-shared/composables/useComplexModelWithChanges";
import type {
  OperationalExperience,
  OperationalExperienceOption,
  OperationalOption,
} from "@/types/generated/ExperienceRawServiceApi";

export interface OperationalLinkData {
  operational_id: string;
  name: string;
  code: string;
  options: OperationalOption[];
}

const operationalLinkSchema = z.object({
  operational_id: z.string().min(1),
  name: z.string().min(1),
  code: z.string().min(1),
  options: z.array(z.object({ option_id: z.string(), code: z.string(), name: z.string() })).min(1),
});

export function useRawOperationalLinkFactory(initialData?: OperationalLinkData) {
  const defaultData: OperationalLinkData = initialData ?? {
    operational_id: "",
    name: "",
    code: "",
    options: [],
  };

  const initialValue = ref<OperationalLinkData>(defaultData);
  const { model, hasChanges, isFilled, reset } = useComplexModelModelWithChanges(initialValue);

  const { isValid, errors } = useZodValidation(operationalLinkSchema, model);

  return {
    data: model,
    hasChanges,
    isFilled,
    isValid,
    errors,
    reset,
    setOperationalExperience,
    isOptionSelected,
    toggleOption,
  };

  function setOperationalExperience(
    operational: { operational_id: string; name: string; code: string },
    availableOptions: OperationalExperienceOption[]
  ) {
    model.value.operational_id = operational.operational_id;
    model.value.name = operational.name;
    model.value.code = operational.code;
    // Select all available options by default when linking an operational
    model.value.options = availableOptions.map((opt) => ({
      option_id: opt.option_id,
      code: opt.code ?? "",
      name: opt.name,
    }));
  }

  function isOptionSelected(optionId: string): boolean {
    return model.value.options.some((opt) => opt.option_id === optionId);
  }

  function toggleOption(option: OperationalExperienceOption) {
    const index = model.value.options.findIndex((opt) => opt.option_id === option.option_id);
    if (index === -1) {
      model.value.options.push({
        option_id: option.option_id,
        code: option.code ?? "",
        name: option.name,
      });
    } else {
      model.value.options.splice(index, 1);
    }
  }
}

export type RawOperationalLinkInstance = ReturnType<typeof useRawOperationalLinkFactory>;

/**
 * Maps an OperationalExperience from the API response to the factory's initial data format.
 */
export function mapOperationalExperienceToLinkData(experience: OperationalExperience): OperationalLinkData {
  return {
    operational_id: experience.operational_id,
    name: experience.name,
    code: experience.code,
    options: experience.options.map((opt) => ({
      option_id: opt.option_id,
      code: opt.code,
      name: opt.name,
    })),
  };
}
